/**
 * LineagePage
 *
 * Data lineage visualization and analysis page
 * Integrates existing Cytoscape visualization with modern UI
 */

import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Network, Download, RefreshCw, Maximize2, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import {
  useLineageGraph,
  useDatasetLineage,
  useLineageSearch,
} from '@/features/lineage/hooks/useLineage';
import { LineageTimeTravelPanel } from '@/features/lineage/components';
import type {
  LineageDirection,
  LineageOptions,
  LineageGraph,
} from '@/features/lineage/types/lineage.types';

export const LineagePage: React.FC = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get entity ID from URL params or default
  const entityId = searchParams.get('entity') || null;
  const isDataset = searchParams.get('type') === 'dataset';

  // Lineage options
  const [direction, setDirection] = useState<LineageDirection>('both');
  const [depth, setDepth] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTimeTravel, setShowTimeTravel] = useState(false);
  const [timeTravelGraph, setTimeTravelGraph] = useState<LineageGraph | null>(null);
  const [timeTravelTimestamp, setTimeTravelTimestamp] = useState<string | null>(null);

  const lineageOptions: LineageOptions = {
    direction,
    depth,
    includeMetadata: true,
    includeInactive: false,
  };

  // Fetch lineage data. Call both hooks unconditionally (rules-of-hooks);
  // the inactive one gets a null id so its query stays disabled — no double fetch.
  const datasetMode = isDataset && !!entityId;
  const datasetLineage = useDatasetLineage(datasetMode ? entityId : null, direction, depth);
  const graphLineage = useLineageGraph(datasetMode ? null : entityId, lineageOptions);
  const { data: lineageData, isLoading, error, refetch } = datasetMode ? datasetLineage : graphLineage;

  const { data: searchResults } = useLineageSearch(searchQuery);

  const handleDirectionChange = (value: string) => {
    setDirection(value as LineageDirection);
  };

  const handleDepthChange = (value: string) => {
    setDepth(parseInt(value, 10));
  };

  const handleEntitySelect = (selectedEntityId: string) => {
    setSearchParams({ entity: selectedEntityId });
  };

  const handleExport = async (format: 'png' | 'svg' | 'json') => {
    // TODO: Implement export functionality
    toast({
      title: 'Export started',
      description: `Exporting lineage graph as ${format.toUpperCase()}...`,
    });
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Handle time travel completion
  const handleTimeTravelComplete = useCallback((graph: LineageGraph, timestamp: string) => {
    setTimeTravelGraph(graph);
    setTimeTravelTimestamp(timestamp);
    toast({
      title: 'Time travel complete',
      description: `Viewing lineage as of ${new Date(timestamp).toLocaleString()}`,
    });
  }, [toast]);

  // Exit time travel mode
  const exitTimeTravel = useCallback(() => {
    setTimeTravelGraph(null);
    setTimeTravelTimestamp(null);
    setShowTimeTravel(false);
  }, []);

  // Get active graph data (time travel or current)
  const activeGraph = timeTravelGraph || lineageData;

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-full h-[calc(100vh-4rem)] py-8 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card className="h-[calc(100%-8rem)]">
          <CardContent className="h-full flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="text-muted-foreground">Loading lineage graph...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-5xl py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load lineage data. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!entityId) {
    return (
      <div className="container mx-auto max-w-5xl py-8 space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Network className="h-8 w-8" />
            Data Lineage
          </h1>
          <p className="text-muted-foreground">
            Visualize data flow and dependencies across your lakehouse
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Search for an Entity</CardTitle>
            <CardDescription>
              Find a dataset, transformation, or model to view its lineage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Search datasets, transformations, models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {searchResults && searchResults.length > 0 && (
              <div className="space-y-2">
                <Label>Results</Label>
                <div className="grid gap-2">
                  {searchResults.map((result: any) => (
                    <Button
                      key={result.id}
                      variant="outline"
                      className="justify-start"
                      onClick={() => handleEntitySelect(result.id)}
                    >
                      <Network className="h-4 w-4 mr-2" />
                      {result.name} ({result.type})
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-full h-[calc(100vh-4rem)] py-4 space-y-4">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Network className="h-6 w-6" />
            Data Lineage
          </h1>
          <p className="text-sm text-muted-foreground">
            {lineageData?.nodes.length || 0} nodes, {lineageData?.edges.length || 0} edges
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Direction selector */}
          <Select value={direction} onValueChange={handleDirectionChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upstream">Upstream</SelectItem>
              <SelectItem value="downstream">Downstream</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>

          {/* Depth selector */}
          <Select value={depth.toString()} onValueChange={handleDepthChange}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Depth" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Depth: 1</SelectItem>
              <SelectItem value="2">Depth: 2</SelectItem>
              <SelectItem value="3">Depth: 3</SelectItem>
              <SelectItem value="5">Depth: 5</SelectItem>
              <SelectItem value="10">Depth: 10</SelectItem>
            </SelectContent>
          </Select>

          {/* Action buttons */}
          <Button
            variant={showTimeTravel ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowTimeTravel(!showTimeTravel)}
            title="Time Travel"
          >
            <History className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={!!timeTravelGraph}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('png')}
          >
            <Download className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Time Travel Mode Indicator */}
      {timeTravelTimestamp && (
        <Alert variant="warning" className="flex items-center justify-between">
          <History className="h-4 w-4" />
          <AlertDescription className="flex-1 ml-2">
            <span className="text-sm font-medium">
              Time Travel Mode: Viewing lineage as of {new Date(timeTravelTimestamp).toLocaleString()}
            </span>
          </AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            onClick={exitTimeTravel}
          >
            Exit Time Travel
          </Button>
        </Alert>
      )}

      {/* Main Content Area with Graph and Optional Time Travel Panel */}
      <div className={`flex gap-4 ${timeTravelTimestamp ? 'h-[calc(100%-8rem)]' : 'h-[calc(100%-5rem)]'}`}>
        {/* Time Travel Panel (collapsible sidebar) */}
        {showTimeTravel && (
          <div className="w-80 flex-shrink-0">
            <LineageTimeTravelPanel
              entityId={entityId || undefined}
              onTimeTravelComplete={handleTimeTravelComplete}
              className="h-full"
            />
          </div>
        )}

        {/* Lineage Graph Canvas */}
        <Card className="flex-1">
          <CardContent className="h-full p-6">
            <div className="h-full rounded-lg border bg-muted/50 flex items-center justify-center">
              <div className="text-center space-y-4 p-8">
                <Network className="h-16 w-16 text-muted-foreground mx-auto" />
                <div className="space-y-2">
                  <p className="text-lg font-medium">Cytoscape Graph Visualization</p>
                  <p className="text-sm text-muted-foreground max-w-md">
                    The interactive lineage graph will be rendered here using Cytoscape.js.
                    This is a placeholder for the full migration.
                  </p>
                  {activeGraph && (
                    <div className="text-xs text-muted-foreground">
                      <p>Loaded {activeGraph.nodes.length} nodes and {activeGraph.edges.length} edges</p>
                      <p className="mt-2">Direction: {direction} | Depth: {depth}</p>
                      {timeTravelTimestamp && (
                        <p className="mt-1 text-warning">
                          Historical view from {new Date(timeTravelTimestamp).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
